import store from 'store'
import { useContext } from 'react'
import camelize from 'camelize'
import { filterActiveUsers } from 'modules/userFiltering'

import { getCurrentUserSelector } from 'selectors'
import {
  getUsers,
  getHigherUpUsers,
  getReadOnlyUsers,
  getVisibleUsers,
  getVisibleActiveUsersForCompanyOwner,
  getVisibleActiveUsersForDealOwner,
  getVisibleActiveUsersForPersonOwner,
  rewindUserV3ToOrig
} from 'selectors/Account'
import { getVisibleActiveUsers } from 'VisibleUsers/selectors'
import { getSelectedCurrencies } from 'ducks'

import { FormContext } from 'add_edit_modals/context'
import ChangedAttributesFinder from 'add_edit_modals/ChangedAttributesFinder'

// Evaluated lazily to ensure window.Features is available (set by server-rendered HAML)
export const getResourceTypes = () => ['company', 'deal', 'person', 'calendar_task', 'calendar_event', 'quick_add', ...(window.Features?.subtasks ? ['sub_calendar_entries'] : []), 'project', 'project_milestones', 'project_milestone', 'milestone_tasks', 'milestone_events']
export const customizableTypes = ['company', 'deal', 'person']

// Nested resource types - these follow Rails nested attributes convention
// attributeName is derived as `${resourceType}_attributes`
// Evaluated lazily to ensure window.Features is available (set by server-rendered HAML)
export const getNestedResourceTypes = () => [...(window.Features?.subtasks ? ['sub_calendar_entries'] : []), 'project_milestones', 'milestone_tasks', 'milestone_events']

// Delimiter used in nested resource field IDs (e.g., "0.name" where 0 is the index and name is the field)
export const NESTED_RESOURCE_FIELD_DELIMITER = '.'

// Check if resourceType is a nested resource
export const isNestedResource = (resourceType) => {
  return getNestedResourceTypes().includes(resourceType)
}

// Check if key is a numeric index (indicating a nested resource item in metadata/values)
export const isNestedResourceIndex = (key) => /^\d+$/.test(key)

// Check if fieldId is a nested resource field (e.g., "0.name")
export const isNestedResourceFieldId = (fieldId) =>
  fieldId?.includes(NESTED_RESOURCE_FIELD_DELIMITER)

// Parse nested resource fieldId into index and actual field name
// e.g., "0.name" -> { index: 0, actualFieldId: "name" }
export const parseNestedResourceFieldId = (fieldId) => {
  const [index, actualFieldId] = fieldId.split(NESTED_RESOURCE_FIELD_DELIMITER)
  return { index: parseInt(index, 10), actualFieldId }
}

export const isDefaultOpen = (id) => {
  const { resourceType } = useContext(FormContext)

  if (!window.User.hasOwnProperty(resourceType + 'JsGroupState')) return false
  return window.User[resourceType + 'JsGroupState'][id] === 'opened'
}

export const capitalize = (word) => word[0].toUpperCase() + word.substr(1)
export const classify = (word) => capitalize(camelize(word))

const addCurrentOwner = (possibleOwners, currentOwnerId) => {
  const visibleUsers = getVisibleUsers(store.getState())
  const currentOwner = visibleUsers.find(u => u.id === currentOwnerId)
  if (currentOwner && !possibleOwners.find(o => o.id === currentOwner.id)) {
    possibleOwners = possibleOwners.concat(currentOwner)
  }

  return possibleOwners
}

const mapOwners = (possibleOwners) => {
  return possibleOwners.map(u => ({ label: u.name, value: u.id }))
}

export const possibleOwnersFor = (resourceType, fieldValue) => {
  const visibleActiveUsersForCompanyOwner = getVisibleActiveUsersForCompanyOwner(store.getState())
  const visibleActiveUsersForDealOwner = getVisibleActiveUsersForDealOwner(store.getState())
  const visibleActiveUsersForPersonOwner = getVisibleActiveUsersForPersonOwner(store.getState())
  const visibleActiveUsers = getVisibleActiveUsers(store.getState())

  let possibleOwners = {
    company: visibleActiveUsersForCompanyOwner,
    deal: visibleActiveUsersForDealOwner,
    person: visibleActiveUsersForPersonOwner
  }[resourceType]

  if (!possibleOwners) {
    possibleOwners = visibleActiveUsers
  }

  return mapOwners(addCurrentOwner(possibleOwners, fieldValue))
}

export const visibleUsersForEntity = (entity) => {
  if (entity) {
    // TODO: remove the line below once we get rid of the Backbone code
    //  (not sure if somewhere in the code we are still passing the Backbone models)
    if (entity.attributes) { entity = entity.attributes }

    const accountUsers = getUsers(store.getState())
    let users = accountUsers.filter(user => entity.possible_notify_user_ids.includes(user.id))

    if (entity.user_id !== undefined) {
      const entityOwner = accountUsers.find(user => user.id === entity.user_id)
      if (entityOwner) {
        users = [...users, entityOwner]
      }
    }

    const sharedUsers = users.filter(user => entity.shared_user_ids.includes(user.id))
    users = [...users, ...sharedUsers]

    const activeHigherUpUsers = filterActiveUsers(getHigherUpUsers(store.getState()))
    users = [...users, ...activeHigherUpUsers]

    const currentUserId = getCurrentUserSelector(store.getState()).id
    const currentUser = accountUsers.find(user => user.id === currentUserId)
    users = [currentUser, ...users]

    const readOnlyUserIds = getReadOnlyUsers(store.getState()).map(u => u.id)
    users = users.filter(user => !readOnlyUserIds.includes(user.id))

    users = users.filter((user, index, self) => index === self.findIndex(u => u.id === user.id))

    return users.map(user => rewindUserV3ToOrig(user))
  } else {
    return getVisibleActiveUsers(store.getState())
  }
}

export const possibleTaskEventOwners = (entity, fieldValue) => {
  const possibleOwners = visibleUsersForEntity(entity)
  return mapOwners(addCurrentOwner(possibleOwners, fieldValue))
}

export const possibleCurrencies = (selectedCurrencyId) => {
  const selectedCurrencies = getSelectedCurrencies(store.getState())
  let possibleCurrencies = [...selectedCurrencies]
  possibleCurrencies = possibleCurrencies.sort((a, b) => a.position - b.position).map(c => ({ label: `${c.code} ${c.symbol} - ${c.name}`, value: c.id }))

  if (selectedCurrencyId && !possibleCurrencies.map(c => c.value).includes(selectedCurrencyId)) {
    possibleCurrencies.unshift({ label: selectedCurrencyId, value: selectedCurrencyId })
  }

  return possibleCurrencies
}

export const toBool = (val) => [true, 'true', 1, '1'].includes(val)

export const resourceToUrlMap = {
  person: '/people',
  deal: '/deals',
  company: '/companies',
  customer: '/companies',
  calendar_event: '/calendar_events',
  calendar_task: '/calendar_tasks',
  quick_add: '/quick_add',
  project: '/api/v3/projects',
  project_milestone: '/api/v3/project_milestones'
}

export const humanizeResourceType = (resourceType) => {
  switch (resourceType) {
    case 'calendar_task':
      return 'Calendar Task'
    case 'calendar_event':
      return 'Calendar Event'
    case 'quick_add':
      return 'Quick Add'
    case 'project_milestone':
      return 'Milestone'
    default:
      return capitalize(resourceType)
  }
}

export const valuesToReject = (resourceType) => {
  switch (resourceType) {
    case 'deal':
      return [
        'is_allowed_to_delete',
        'can_be_reassigned_by_current_user',
        'sharees',
        'stage_name',
        'company_path',
        'next_entry',
        'primary_contact',
        'possible_notify_user_ids',
        'documents',
        'notes',
        'people',
        'commission',
        'expected_close_date_dummy',
        'latest_activity',
        'profile_layout',
        'source',
        'deal_pipeline',
        'days_active',
        'time_to_close',
        'deal_stage',
        'deal_status',
        'import',
        'user',
        'company',
        'owner_name',
        'status_color',
        'status_name',
        'updated_at',
        'days_in_stage',
        'date_of_first_activity',
        'hours_to_first_activity',
        'days_since_last_activity',
        'latest_deal_stage_change',
        'customer_days_in_health',
        'health_score_changed_at',
        'currency',
        'revenue_type',
        'owner_deleted_at',
        'can_be_merged_by_current_user',
        'deal_weighted_forecast',
        'deal_time_to_dollar',
        'deal_days_active',
        'deal_time_to_close',
        'product_lines',
        'created_by_user_id',
        'updated_by_user_id',
        'deal_time_to_dollar_change',
        'number_of_days_in_stages',
        'activities_created_count',
        'days_without_activity',
        'tags'
      ]
    case 'person':
      return [
        'is_allowed_to_delete',
        'can_be_reassigned_by_current_user',
        'full_name',
        'company_path',
        'company_address',
        'home_address',
        'latest_activity',
        'lead_status',
        'profile_layout',
        'source',
        'work_address',
        'possible_notify_user_ids',
        'notes',
        'deals',
        'documents',
        'tags',
        'work_address_google_maps_url',
        'cached_tag_ids',
        'owner_name',
        'updated_at',
        'has_deals',
        'date_of_first_activity',
        'hours_to_first_activity',
        'days_since_last_activity',
        'userCurrencySymbol',
        'owner_deleted_at',
        'created_by_user_id'
      ]
    case 'company':
      return [
        'can_be_deleted_by_current_user',
        'can_be_merged_by_current_user',
        'address',
        'pdf_company_path',
        'possible_notify_user_ids',
        'notes',
        'deals',
        'people',
        'documents',
        'total_pipeline',
        'won_value',
        'worked_on_by',
        'owner_name',
        'loggedUserIsExecutive',
        'updated_at',
        'people_for_notes_association',
        'has_deals',
        'has_people',
        'customer',
        'userCurrencySymbol',
        'owner_deleted_at',
        'tags',
        'hierarchy_details'
      ]
    case 'calendar_event':
      return [
        'start_time',
        'end_time',
        'primary_contact',
        'contact',
        '_date_select_for_start_time_date',
        '_date_select_for_end_time_date',
        '_date_select_for_recurrence_end',
        '_date_select_for_due_date',
        'primary_contact_id',
        'company_name',
        'company',
        'possible_notify_user_ids',
        'shared_user_ids',
        'deal_id',
        'person_id',
        'contact_id',
        'lead_id',
        'deal_name',
        'person_name',
        'contact_name',
        'lead_name',
        'deal',
        'lead',
        'updated_at',
        'created_by_user_id',
        'repeat_this_entry'
      ]
    case 'calendar_task':
      return [
        'start_time',
        'end_time',
        'primary_contact',
        '_date_select_for_start_time_date',
        '_date_select_for_end_time_date',
        '_date_select_for_recurrence_end',
        '_date_select_for_due_date',
        'primary_contact_id',
        'company_name',
        'company',
        'created_at',
        'contact',
        'updated_at',
        'possible_notify_user_ids',
        'shared_user_ids',
        'deal_id',
        'person_id',
        'contact_id',
        'lead_id',
        'deal_name',
        'person_name',
        'contact_name',
        'lead_name',
        'deal',
        'lead',
        'repeat_this_entry',
        'project_milestone'
      ]
    case 'project':
      return [
        'is_archived',
        'is_active',
        'counts',
        'deals',
        'companies',
        'people',
        'collaborators',
        'milestones',
        'owner',
        'updated_at',
        'created_at',
        'archived_at'
      ]
    case 'project_milestone':
      return [
        'project',
        'status',
        'updated_at',
        'created_at'
      ]
    default:
      return []
  }
}

export const fieldIdMapped = (fieldId) => {
  if (fieldId === 'is_lead') return 'type'
  if (fieldId === 'deal_revenue_type') return 'revenue_type_id'
  if (fieldId === 'deal_primary_contact_id') return 'primary_contact_id'
  if (fieldId === 'company_id') return 'company_name'
  return fieldId
}

export const inverseFieldIdMapped = (fieldId) => {
  if (fieldId === 'type') return 'is_lead'
  if (fieldId === 'revenue_type_id') return 'deal_revenue_type'
  if (fieldId === 'primary_contact_id') return 'deal_primary_contact_id'
  if (fieldId === 'company_name') return 'company_id'
  return fieldId
}

export const addMandatoryFields = (resourceType, changedAttrs, currentValues) => {
  if (resourceType === 'calendar_event' || resourceType === 'calendar_task') {
    if (changedAttrs.recurrence_end || changedAttrs.recurrence_frequency) {
      changedAttrs.recurrence_frequency ||= currentValues[resourceType].recurrence_frequency
      changedAttrs.recurrence_end ||= currentValues[resourceType].recurrence_end
    }
  }

  return changedAttrs
}

export const withQuickAddForm = (forms) => {
  forms.quick_add = [
    {
      id: 'company',
      label: 'Company',
      parent_id: null,
      opened: true,
      system: true,
      display_as_field: false,
      group: true,
      hide_group_header: false,
      children: forms.company_quick_add
    },
    {
      id: 'deal',
      label: 'Deal',
      parent_id: null,
      opened: true,
      system: true,
      display_as_field: false,
      group: true,
      hide_group_header: false,
      children: forms.deal_quick_add
    },
    {
      id: 'person',
      label: 'Person',
      parent_id: null,
      opened: true,
      system: true,
      display_as_field: false,
      group: true,
      hide_group_header: false,
      children: forms.person_quick_add
    }
  ]
  return forms
}

/**
 * Transform nested resources to Rails nested attributes format before submission.
 * Only includes new, changed, or deleted nested resources (optimization to reduce payload).
 *
 * @example
 * // Input (frontend form structure):
 * {
 *   calendar_task: { id: 123, name: "Parent Task" },
 *   sub_calendar_entries: [
 *     { id: 456, name: "Updated Subtask" },  // Changed - will include only changed fields
 *     { id: 457, name: "Unchanged" },        // No changes - will be skipped
 *     { name: "New Subtask" },               // New - will include all fields
 *     { id: 458, _destroy: true }            // Deleted - will include id + _destroy only
 *   ]
 * }
 *
 * // Output (Rails nested_attributes format):
 * {
 *   calendar_task: {
 *     id: 123,
 *     name: "Parent Task",
 *     sub_calendar_entries_attributes: [
 *       { id: 456, name: "Updated Subtask" },
 *       { name: "New Subtask" },
 *       { id: 458, _destroy: true }
 *     ]
 *   }
 * }
 */
export const transformNestedResourcesForSubmission = (formValues, oldValues = {}) => {
  const transformed = { ...formValues }

  // Find all nested resource types in the form values
  Object.keys(transformed).forEach(resourceType => {
    if (!isNestedResource(resourceType)) return

    // Derive Rails attribute name from convention: resourceType + '_attributes'
    const attributeName = `${resourceType}_attributes`
    const nestedResources = transformed[resourceType]

    // Find parent by checking which non-nested resourceType exists in formValues
    const parentType = Object.keys(transformed).find(key =>
      !isNestedResource(key) && transformed[key] && typeof transformed[key] === 'object'
    )

    if (parentType) {
      // Get old nested resources for comparison
      const oldNestedResources = oldValues[resourceType] || []

      // Rails accepts arrays directly! No need to convert to hash format
      // Just filter and transform the nested resources as needed
      const nestedResourceAttributes = []

      if (Array.isArray(nestedResources)) {
        nestedResources.forEach((nestedResource) => {
          // Skip new records marked for destruction (no id + _destroy = true)
          // No point sending them to Rails since they were never saved
          if (!nestedResource.id && nestedResource._destroy) return

          // Include if: new record (no id)
          if (!nestedResource.id) {
            nestedResourceAttributes.push(nestedResource)
            return
          }

          // Include if: marked for deletion (has id + _destroy)
          if (nestedResource._destroy) {
            // Only send id and _destroy flag - Rails doesn't need other fields for deletion
            nestedResourceAttributes.push({ id: nestedResource.id, _destroy: true })
            return
          }

          // Existing record (has id, not marked for deletion)
          // Find corresponding old nested resource by id
          const oldNestedResource = oldNestedResources.find(old => old.id === nestedResource.id)

          if (!oldNestedResource) {
            // Old nested resource not found - include it (shouldn't happen but be safe)
            nestedResourceAttributes.push(nestedResource)
            return
          }

          // Compare with old nested resource to detect changes
          const changedAttrs = new ChangedAttributesFinder(oldNestedResource).changes({ ...nestedResource })

          // Only include if something changed
          if (Object.keys(changedAttrs).length > 0) {
            // Include the id so Rails knows which record to update
            nestedResourceAttributes.push({ id: nestedResource.id, ...changedAttrs })
          }
          // If nothing changed, skip this nested resource entirely
        })
      }

      // Use array format directly - Rails accepts this!
      transformed[parentType][attributeName] = nestedResourceAttributes

      // Remove from top level
      delete transformed[resourceType]
    }
  })

  return transformed
}

/**
 * Transform API response with nested relations to frontend form structure.
 * Extracts nested arrays from parent object to top-level for form handling.
 *
 * @example
 * // Input (API response):
 * {
 *   calendar_task: {
 *     id: 123,
 *     name: "Parent Task",
 *     sub_calendar_entries: [
 *       { id: 456, name: "Subtask 1", owner: { full_name: "John" } }
 *     ]
 *   }
 * }
 *
 * // Output (frontend form structure):
 * {
 *   calendar_task: { id: 123, name: "Parent Task" },
 *   sub_calendar_entries: [
 *     { id: 456, name: "Subtask 1", owner: { full_name: "John" } }
 *   ]
 * }
 */
export const transformNestedResourcesFromResponse = (responseData) => {
  // Deep clone to avoid mutating the input
  const transformed = JSON.parse(JSON.stringify(responseData))

  // Find nested resources in the response
  getNestedResourceTypes().forEach(nestedResourceType => {
    // Find parent that contains nested data
    const parentType = Object.keys(transformed).find(key =>
      !isNestedResource(key) &&
      transformed[key] &&
      typeof transformed[key] === 'object' &&
      transformed[key][nestedResourceType] !== undefined
    )

    if (parentType && transformed[parentType][nestedResourceType]) {
      // Extract nested array from parent
      const nestedArray = transformed[parentType][nestedResourceType]

      // Only extract if it's not empty
      if (Array.isArray(nestedArray) && nestedArray.length > 0) {
        // Move nested array to top level for form handling
        // No need to clean read-only fields - ChangedAttributesFinder handles this
        // by only submitting fields that have actually changed
        transformed[nestedResourceType] = nestedArray

        // Remove from parent (clean up structure)
        delete transformed[parentType][nestedResourceType]
      }
    }
  })

  return transformed
}
